import { css } from 'styled-components';
import { breakpoints } from '../breakpoints';
import type { Breakpoint, Responsive } from './responsive.types';

/**
 * Type guard: checks if a responsive prop value is a breakpoint object
 * (as opposed to a plain scalar value).
 */
export function isResponsive<T extends string>(
    value: Responsive<T> | undefined
): value is Partial<Record<Breakpoint, T>> {
    return typeof value === 'object' && value !== null;
}

/**
 * Ordered breakpoints for mobile-first media queries.
 * `initial` has no minWidth (base styles, no media query).
 */
const BREAKPOINT_MIN_WIDTHS: Array<{
    key: Breakpoint;
    minWidth: number | null;
}> = [
    { key: 'initial', minWidth: null },
    { key: 'small', minWidth: breakpoints.small },
    { key: 'medium', minWidth: breakpoints.medium },
    { key: 'large', minWidth: breakpoints.large },
    { key: 'xLarge', minWidth: breakpoints.xLarge }
];

/**
 * Breakpoint keys in mobile-first order. Derived from `BREAKPOINT_MIN_WIDTHS` so the
 * ordering has a single source of truth. Internal — consumers get `Breakpoint` instead.
 */
export const BREAKPOINT_ORDER: readonly Breakpoint[] =
    BREAKPOINT_MIN_WIDTHS.map(({ key }) => key);

/**
 * Generates CSS for a responsive prop value.
 * Supports both static values and responsive breakpoint objects.
 * Generic — works with any value type and any CSS properties.
 *
 * @param value - A value or responsive breakpoint object
 * @param cssProperties - One or more CSS property names to set
 * @param resolver - Function to resolve a value to a CSS string
 *
 * @example
 * // Static: responsiveCss('24', ['padding'], key => spacing[key])
 * // → `padding: 24px;`
 *
 * // Responsive: responsiveCss({ initial: '8', medium: '16' }, ['padding'], ...)
 * // → `padding: 8px; @media (min-width: 768px) { padding: 16px; }`
 */
export function responsiveCss<T extends string>(
    value: Responsive<T> | undefined,
    cssProperties: string[],
    resolver: (key: T) => string | undefined
) {
    if (value === undefined) {
        return '';
    }

    if (!isResponsive(value)) {
        const resolved = resolver(value);
        if (!resolved) {
            return '';
        }
        return css`
            ${cssProperties.map(prop => `${prop}: ${resolved};`).join('\n')}
        `;
    }

    // Responsive object — generate base + media queries
    const blocks: string[] = [];

    for (const { key, minWidth } of BREAKPOINT_MIN_WIDTHS) {
        const breakpointValue = value[key] as T | undefined;
        if (breakpointValue === undefined) {
            continue;
        }
        const resolved = resolver(breakpointValue);
        if (!resolved) {
            continue;
        }
        const declarations = cssProperties
            .map(prop => `${prop}: ${resolved};`)
            .join(' ');
        if (minWidth === null) {
            blocks.push(declarations);
        } else {
            blocks.push(
                `@media (min-width: ${minWidth}px) { ${declarations} }`
            );
        }
    }

    return css`
        ${blocks.join('\n')}
    `;
}

/**
 * Shorthand for responsiveCss with passthrough (no token resolution).
 * Used for CSS values like display, grid-template-columns, flex-direction, etc.
 */
export function responsiveValueCss<T extends string>(
    value: Responsive<T> | undefined,
    cssProperties: string[]
) {
    return responsiveCss(value, cssProperties, (v: T) => v);
}
